import { useEffect } from 'react';
import { Accept } from 'react-dropzone';
import { SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import {
  AccentButton,
  NeutralButton,
} from '../../../../../../../shared/Buttons';
import { Entity } from '../../../../../../../types/apiTypes';
import { useScreenView } from '../../../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../../../utils/UserContext';
import { useFleetInsurance } from '../fleet-insurance/useFleetInsurance';
import AssetCondition from './AssetCondition';
import AssetFinancials from './AssetFinancials';
import AssetGeneralInfo from './AssetGeneralInfo';
import { useAssetForm } from './useAssetForm';
import { useAssetMutations } from './useAssetMutations';
import { useVinLookup } from './useVinLookup';
import { AssetFormData, AssetView } from './types';

const ASSETS_ROUTE = '/dashboard/assets';

const acceptFiles: Accept = {
  'image/*': ['.png', '.jpeg', '.jpg', '.heic', '.heif'],
};

type AssetFormProps =
  | { mode: 'create' }
  | {
      mode: 'edit';
      selectedAsset: AssetView | null;
      setSelectedAsset: (asset: AssetView) => void;
      assetImageUrl: string;
      setAssetImageUrl: (url: string) => void;
    };

/**
 * THT-643 — the single decomposed asset form. Replaces CreateAsset.tsx and
 * EditAsset.tsx (and their 3× breakpoint copies) with one responsive layout
 * driven by `mode`. All create/edit behavior and payloads are unchanged; see
 * docs/spec-assetform-decomposition-ui.md for the parity baseline.
 */
export default function AssetForm(props: AssetFormProps) {
  const { mode } = props;
  const isEdit = mode === 'edit';
  // Narrow on `props.mode` directly so the edit-only fields are typed.
  const editProps = props.mode === 'edit' ? props : undefined;

  const navigate = useNavigate();
  const { userRecord } = useUser();
  const { screenView } = useScreenView();
  const selectedAsset = editProps?.selectedAsset ?? null;

  // Create renders standalone, so it must cap its own width to the dashboard
  // content column (matching AssetsHome / the header it sits under) per
  // breakpoint — without this it stretches full-width and overflows the header.
  // Edit is rendered inside AssetDetails, which already constrains the width,
  // so it simply fills that container.
  const createWidth =
    screenView === 'desktop'
      ? 'w-[1148px] max-w-[1148px]'
      : screenView === 'tablet'
        ? 'w-[568px] max-w-[568px]'
        : 'w-[351px] max-w-[351px]';
  const containerWidth = isEdit ? 'w-full' : createWidth;

  // THT-675 — one customer-level lookup, shared by the field that disappears
  // and the payload that has to carry 0 in its place.
  const { isFleetMode: isFleetInsurance } = useFleetInsurance();

  const {
    form,
    toSubmitValues,
    loading,
    setLoading,
    assetImage,
    imageRemoved,
    displayImageUrl,
    showImage,
    handleDrop,
    handleRemoveImage,
  } = useAssetForm({
    mode,
    isFleetInsurance,
    selectedAsset: editProps?.selectedAsset ?? null,
    setSelectedAsset: editProps?.setSelectedAsset,
    assetImageUrl: editProps?.assetImageUrl,
    setAssetImageUrl: editProps?.setAssetImageUrl,
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = form;

  // In edit the asset type is immutable, so gate truck-only fields on the
  // existing asset (matching legacy EditAsset, which used selectedAsset?.asset_type).
  // This also avoids briefly rendering truck-only fields for a trailer before the
  // prefill effect overwrites the 'truck' EDIT default. Create uses the toggle value.
  const watchedAssetType = watch('assetType');
  const assetType = isEdit
    ? selectedAsset?.asset_type ?? watchedAssetType
    : watchedAssetType;
  const status = watch('status');
  const ownershipStatus = watch('ownershipStatus');
  const vinNumber = watch('vinNumber');
  const showPMSField = ownershipStatus !== 'owned';

  const { vinFields, resetVinFields, fetchAssetInfoByVIN } = useVinLookup({
    assetType,
    setValue,
    setLoading,
  });

  const customerId =
    userRecord?.userType === Entity.Customer
      ? userRecord?.customer_id ?? ''
      : '';

  const { submit } = useAssetMutations({
    mode,
    customerId,
    selectedAsset: editProps?.selectedAsset ?? null,
    vinFields,
    assetImage,
    showImage,
    imageRemoved,
    setAssetImageUrl: editProps?.setAssetImageUrl,
    setLoading,
  });

  // Create-only: decode the VIN once the field reaches a full 17 chars; clear
  // the decoded values otherwise. Edit never re-decodes (VIN is immutable).
  useEffect(() => {
    if (mode !== 'create') return;
    if (vinNumber && vinNumber?.length === 17) {
      fetchAssetInfoByVIN(vinNumber);
    } else if (vinNumber?.length !== 17) {
      resetVinFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vinNumber]);

  const onSubmit: SubmitHandler<AssetFormData> = (data) =>
    submit(toSubmitValues(data));

  return (
    <div
      className={`h-full overflow-y-auto overflow-x-hidden bg-white ${containerWidth}`}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex min-h-full w-full flex-col px-4 pt-4"
      >
        <div className="flex w-full flex-col gap-y-[24px] pb-4">
          <AssetGeneralInfo
            mode={mode}
            register={register}
            control={control}
            errors={errors}
            accept={acceptFiles}
            assetImage={assetImage}
            onDrop={handleDrop}
            currentImageUrl={isEdit ? displayImageUrl : undefined}
            onRemoveImage={isEdit ? handleRemoveImage : undefined}
            vinFields={vinFields}
            selectedAsset={selectedAsset}
          />
          <div>
            <h2 className="text-[18px] font-[600] text-primary">
              Operational Information
            </h2>
            <div className="flex w-full flex-col gap-x-[24px] gap-y-4 md:flex-row">
              <AssetFinancials
                register={register}
                control={control}
                errors={errors}
                assetType={assetType}
                isFleetInsurance={isFleetInsurance}
              />
              <AssetCondition
                mode={mode}
                register={register}
                control={control}
                errors={errors}
                assetType={assetType}
                status={status}
                showPMSField={showPMSField}
                selectedAsset={selectedAsset}
              />
            </div>
          </div>
        </div>
        <div className="mt-auto flex h-[80px] max-h-[80px] min-h-[80px] w-full flex-row items-center justify-end gap-x-[16px] border-t-[1px] border-[#EAECF0] align-middle">
          <NeutralButton
            onClick={() => (isEdit ? navigate(-1) : navigate(ASSETS_ROUTE))}
            addClass="rounded-full w-[250px] h-[48px]"
          >
            Cancel
          </NeutralButton>
          <AccentButton addClass="h-[48px] w-[250px] rounded-full" submit={true}>
            {!loading ? (
              isEdit ? (
                'Update Asset'
              ) : (
                'Add Asset'
              )
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loading}
                size={20}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="flex h-full w-full justify-center"
              />
            )}
          </AccentButton>
        </div>
      </form>
    </div>
  );
}
